import { Link } from "react-router-dom";
import type { Post } from "@/types/content";
import SectionHeading from "@/components/feature/SectionHeading";

export default function DailyPick({ items }: { items: Post[] }) {
  const [lead, ...rest] = items;

  return (
    <section aria-labelledby="daily-title" className="w-full py-14 md:py-20">
      <div className="mx-auto w-full px-4 md:px-8">
        <SectionHeading
          eyebrow="每日精选"
          title="高赞长帖，值得慢读"
          description="每晚八点更新一批由编辑人工挑出的长帖，信息密度高、有明确结论，适合安静地读完。"
          action={
            <Link
              to="/topics"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-secondary-500 px-4 py-2.5 text-sm font-medium text-foreground-950 transition-colors hover:bg-secondary-600 whitespace-nowrap"
            >
              看今日全部精选
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
          }
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
          {lead && (
            <article className="flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <Link to={`/post/${lead.id}`} className="block">
                <div className="h-56 w-full overflow-hidden bg-background-200 md:h-72">
                  <img
                    src={lead.cover}
                    alt={`${lead.title} - 每日精选长帖配图`}
                    title={`${lead.title} 高校吃瓜网每日精选`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </Link>
              <div className="flex flex-1 flex-col p-5 md:p-7">
                <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-award-line"></i>
                  </span>
                  编辑推荐 · {lead.school}
                </span>
                <h3 className="mt-3.5 font-heading text-xl font-black leading-snug text-foreground-950 md:text-2xl">
                  <Link to={`/post/${lead.id}`} className="transition-colors hover:text-primary-700">
                    {lead.title}
                  </Link>
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-[15px]">
                  {lead.excerpt}
                </p>
                <blockquote className="mt-5 border-l-2 border-accent-500 pl-4 text-sm italic leading-relaxed text-foreground-700">
                  {lead.quote}
                </blockquote>
                <div className="mt-auto flex items-center gap-4 pt-6 text-xs text-foreground-500">
                  <span>{lead.author}</span>
                  <span className="flex items-center gap-1">
                    <span className="flex h-3.5 w-3.5 items-center justify-center">
                      <i className="ri-thumb-up-line"></i>
                    </span>
                    {lead.likes}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="flex h-3.5 w-3.5 items-center justify-center">
                      <i className="ri-eye-line"></i>
                    </span>
                    {lead.views}
                  </span>
                </div>
              </div>
            </article>
          )}

          <div className="flex flex-col gap-5">
            {rest.map((post) => (
              <article
                key={post.id}
                className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 md:p-5"
              >
                <Link
                  to={`/post/${post.id}`}
                  className="hidden h-24 w-28 shrink-0 overflow-hidden rounded-md bg-background-200 sm:block"
                >
                  <img
                    src={post.cover}
                    alt={`${post.title} 配图`}
                    title={`${post.title} 高校吃瓜网`}
                    className="h-full w-full object-cover object-top"
                  />
                </Link>
                <div className="min-w-0 flex-1">
                  <span className="text-xs text-secondary-800">{post.school}</span>
                  <h3 className="mt-1 font-heading text-base font-bold leading-snug text-foreground-950">
                    <Link
                      to={`/post/${post.id}`}
                      className="transition-colors hover:text-primary-700"
                    >
                      {post.title}
                    </Link>
                  </h3>
                  <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                    {post.excerpt}
                  </p>
                  <div className="mt-3 flex items-center gap-3 text-xs text-foreground-500">
                    <span className="flex items-center gap-1 text-primary-600">
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className="ri-thumb-up-line"></i>
                      </span>
                      {post.likes}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className="ri-chat-3-line"></i>
                      </span>
                      {post.comments}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}